"use client";

import { useCallback, useEffect, useState, useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { TBlogSort } from "@/types/blog";

export interface IBlogFiltersProps {
  tags: string[];
  className?: string;
}

export function BlogFilters({
  tags,
  className,
}: IBlogFiltersProps): React.ReactElement {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [, startTransition] = useTransition();

  const activeTags = (searchParams.get("tags") ?? "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  const sort = (searchParams.get("sort") as TBlogSort) || "date_desc";
  const qParam = searchParams.get("q") ?? "";
  const [search, setSearch] = useState(qParam);

  useEffect(() => {
    setSearch(qParam);
  }, [qParam]);

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams.toString());
      for (const [key, value] of Object.entries(updates)) {
        if (value == null || value === "") {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      }
      params.delete("page");
      startTransition(() => {
        const qs = params.toString();
        router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
      });
    },
    [pathname, router, searchParams]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (search !== qParam) {
        updateParams({ q: search.trim() || null });
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, qParam, updateParams]);

  function toggleTag(tag: string): void {
    const next = activeTags.includes(tag)
      ? activeTags.filter((t) => t !== tag)
      : [...activeTags, tag];
    updateParams({ tags: next.length ? next.join(",") : null });
  }

  function clearFilters(): void {
    setSearch("");
    startTransition(() => router.push(pathname, { scroll: false }));
  }

  const hasFilters =
    activeTags.length > 0 || sort !== "date_desc" || qParam.length > 0;

  return (
    <aside
      className={cn(
        "rounded-xl border border-border bg-card p-5",
        className
      )}
      aria-label="Blog filters"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-foreground">Filter</h2>
        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-primary underline-offset-2 hover:underline"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="mt-4 grid gap-2">
        <Label htmlFor="blog-search">Search</Label>
        <Input
          id="blog-search"
          type="search"
          placeholder="Search articles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="mt-4 grid gap-2">
        <Label htmlFor="blog-sort">Sort</Label>
        <Select
          value={sort}
          onValueChange={(value) =>
            updateParams({ sort: value === "date_desc" ? null : value })
          }
        >
          <SelectTrigger id="blog-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date_desc">Newest first</SelectItem>
            <SelectItem value="date_asc">Oldest first</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {tags.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-foreground">Tags</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {tags.map((tag) => {
              const active = activeTags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  aria-pressed={active}
                >
                  <Badge variant={active ? "default" : "secondary"}>
                    {tag}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
}
